import {BLOCK_ACTION_META_KEY} from "../constants.ts";
import type {BlockAction} from "../api/presentations.ts";
import type {CreativeEngine, DesignBlockId} from "@cesdk/cesdk-js";
import {getBlocksRecursive} from "./slideUtilities.ts";

export const filterActionsForSlide = (slideId: number, actions: BlockAction[]): BlockAction[] => {
    if (!actions?.length)
        return [];

    return actions.filter(action => action.slideId === slideId);
};

export const isInteractiveBlockAction = (
    action?: Pick<BlockAction, 'clickActionType' | 'clickActionData'> | null
): boolean => {
    const type = String(action?.clickActionType ?? '').trim();
    const audioUrl = String(action?.clickActionData?.audio?.url ?? '').trim();
    return (type.length > 0 && type !== 'noAction')
        || action?.clickActionData?.['store'] === true
        || audioUrl.length > 0;
};

export const isValidGoToSlideTarget = (target: unknown, slideIds: Set<number>): boolean => {
    const value = String(target ?? '').trim();
    if (!value) {
        return false;
    }

    if (value === 'nextSlide' || value === 'previousSlide') {
        return true;
    }

    const slideId = Number(value);
    return Number.isInteger(slideId) && slideIds.has(slideId);
};

export const hasValidNavigationTarget = (
    action?: Pick<BlockAction, 'clickActionType' | 'clickActionTarget' | 'clickActionData'> | null,
    slideIds: Set<number> = new Set()
): boolean => {
    const type = String(action?.clickActionType ?? '').trim();

    if (type === 'goToSlide') {
        return isValidGoToSlideTarget(action?.clickActionTarget, slideIds);
    }

    const finalAction = action?.clickActionData?.['finalAction'];
    if (
        type === 'finishPresentation'
        && finalAction
        && typeof finalAction === 'object'
        && String((finalAction as Record<string, unknown>)['type'] ?? '').trim() === 'goToSlide'
    ) {
        return isValidGoToSlideTarget(action?.clickActionTarget, slideIds);
    }

    return true;
};

export const filterActionsWithValidTargets = (actions: BlockAction[], slideIds: Set<number>): BlockAction[] => {
    if (!actions?.length) {
        return [];
    }

    return actions.filter(action => hasValidNavigationTarget(action, slideIds));
};

export const getActionForStableId = (stableId: string, actions: BlockAction[]): BlockAction | null => {
    if (!actions) return null;

    return actions.find(action => action.blockId === stableId) ?? null;
}

export const getActionForStableIdAndSlideId = (stableId: string, slideId: number, actions: BlockAction[]): BlockAction | null => {
    if (!actions) return null;

    return actions.find(action => action.blockId === stableId && action.slideId === slideId) ?? null;
}

export const getStableBlockActionId = (engine: CreativeEngine, blockId: DesignBlockId): string | null => {
    try {
        if (!engine.block.hasMetadata(blockId, BLOCK_ACTION_META_KEY))
            return null;

        const value = engine.block.getMetadata(blockId, BLOCK_ACTION_META_KEY);
        if (value.length > 0) {
            return value;
        }
    } catch {
        // ignore
    }
    return null;
};

export function findBlockByStableId(engine: CreativeEngine, stableId :string, except: DesignBlockId | null = null): DesignBlockId | null
{
    const pageId = engine.scene.getCurrentPage();
    if (!pageId)
        return null;

    const blocks: DesignBlockId[] = getBlocksRecursive(engine, pageId);

    for (const id of blocks)
    {
        if (except && id === except)
            continue;

        let meta;
        try {
            meta = engine.block.getMetadata(id, BLOCK_ACTION_META_KEY);
        }
        catch (err) {
            continue;
        }

        if (meta === stableId)
            return id;
    }

    return null;
}

export function findBlockByStableIdForPage(engine: CreativeEngine, pageId: DesignBlockId,  stableId :string, except: DesignBlockId | null = null): DesignBlockId | null
{
    if (!pageId)
        return null;

    const blocks: DesignBlockId[] = getBlocksRecursive(engine, pageId);

    for (const id of blocks)
    {
        if (except && id === except)
            continue;

        let meta;
        try {
            meta = engine.block.getMetadata(id, BLOCK_ACTION_META_KEY);
        }
        catch (err) {
            continue;
        }

        if (meta === stableId)
            return id;
    }

    return null;
}

export const generateStableBlockActionId = (): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    const rand = Math.random().toString(36).slice(2, 10);
    const ts = Date.now().toString(36);
    return `block-${ts}-${rand}`;
};
